import { Button, CircularProgress, Typography } from '@mui/material';
import { AppSelectBox, FieldRow } from 'components/FormElements';
import { FC, useState } from 'react';
import useKiEligibleFunds from 'hooks/data/fund/useKiEligibleFunds';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';

type Props = {
  onNext: (fundDetails: {
    fundId: string;
    fundName: string;
    fundCloseDate: Date;
  }) => void;
  onBack: () => void;
  isEditMode?: boolean;
  defaultFundId?: string;
};

const FundSelector: FC<Props> = ({
  onNext,
  onBack,
  isEditMode,
  defaultFundId,
}) => {
  const nonFullyDeployedTrancheDisclosure = useDisclosure();
  const tranches = useKiEligibleFunds();
  const [selectedFundId, setSelectedFundId] = useState<string | undefined>(
    defaultFundId,
  );
  const [error, setError] = useState<string>();

  const handleNext = (proceedWithoutFullyDeployedFund?: boolean) => {
    setError(undefined);

    if (!selectedFundId) {
      setError('Please select a tranche');
      return;
    }
    const selectedFund = tranches.data?.find(
      (tranche) => tranche._id === selectedFundId,
    );
    if (!selectedFund) {
      setError("Can't find selected tranche");
      return;
    }
    if (!selectedFund.fullyDeployed && !proceedWithoutFullyDeployedFund) {
      nonFullyDeployedTrancheDisclosure.onOpen();
      return;
    }

    onNext({
      fundId: selectedFundId,
      fundName: selectedFund.fundName,
      fundCloseDate: new Date(selectedFund.closedDate),
    });
  };

  const availableTranches =
    tranches.data?.filter(
      (tranche) => !tranche.hasCertificate || tranche._id === defaultFundId,
    ) ?? [];

  return (
    <Spacing>
      <AlertDialog
        open={nonFullyDeployedTrancheDisclosure.isOpen}
        onClose={nonFullyDeployedTrancheDisclosure.onClose}
        title="Tranche in deployment"
        content="As this tranche is still in deployment, its EIS5s will not list future investments or deals currently awaiting tax certificate processing. Do you wish to continue anyway?"
        btnLabels={{
          confirm: 'Proceed',
          cancel: 'Go back',
        }}
        onConfirm={() => {
          const proceedWithoutFullyDeployedFund = true;
          handleNext(proceedWithoutFullyDeployedFund);
        }}
      />
      <FieldRow title="Tranche*" centerTitle>
        {tranches.isLoading ? (
          <CircularProgress size={32} />
        ) : (
          <AppSelectBox
            required
            data={availableTranches}
            valueKey="_id"
            disabled={isEditMode}
            name="fundId"
            labelKey="fundName"
            variant="outlined"
            value={selectedFundId}
            error={!!error}
            helperText={error}
            placeholder={
              availableTranches.length
                ? 'Select tranche'
                : 'No KI funds available'
            }
            onChange={(event) => {
              setSelectedFundId(event.target.value as string);
            }}
          />
        )}
      </FieldRow>
      <ButtonGroup>
        <Button color="primary" variant="outlined" onClick={onBack}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => handleNext()}
          disabled={tranches.isLoading}
        >
          Next
        </Button>
      </ButtonGroup>
      <Typography variant="body2">
        <strong>Note:</strong> The system will use tranche EIS3 data to populate
        each EIS5. Generating this EIS5 before a tranche has completed its final
        allocation may lead to omissions on the EIS5.
      </Typography>
    </Spacing>
  );
};

export default FundSelector;
